import { Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NbDialogRef } from '@nebular/theme';
import { Subscription } from 'rxjs';

import { User } from '../../interfaces/user';
import { Globals } from '../../shared/Globals';
import { AuthService } from '../../shared/auth.service';
import { SubmissionService } from '../../shared/submission.service';

type HubTile = 'standard' | 'tailored' | 'letter' | 'both';

/**
 * The "Create documents" hub: a router, not an engine (ADR-0001).
 * A Standard CV stays in this dialog (it hosts the existing generate panel);
 * a Tailored CV / Cover letter / both close the dialog FIRST, then navigate to
 * the routed tailoring intake (the #503 close-before-navigate rule). Inlining
 * the routed workspace components here would create a circular import, so we
 * navigate instead.
 */
@Component({
  selector: 'bst-cv-documents-hub-dialog',
  templateUrl: './cv-documents-hub-dialog.component.html',
  styleUrls: ['./cv-documents-hub-dialog.component.scss'],
  standalone: false,
})
export class CvDocumentsHubDialogComponent implements OnInit, OnDestroy {
  @Input() public userId?: number;
  @Input() public clientId?: number;
  @Input() public vacancyId?: number | null;
  /** CRM: when set, the tailor create call will auto-link the package to this submission. */
  @Input() public submissionId?: number | null;

  /** 'tiles' = the chooser grid; 'standard' = hosting the static generate panel. */
  public mode: 'tiles' | 'standard' = 'tiles';

  public pickedUserId: number | null = null;
  public userControl = new FormControl<User | null>(null);

  private subs: Subscription[] = [];

  constructor(
    private readonly router: Router,
    public readonly auth: AuthService,
    private readonly submissionService: SubmissionService,
    @Optional() private readonly dialogRef: NbDialogRef<CvDocumentsHubDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.pickedUserId = this.userId && this.userId > 0 ? this.userId : null;
    this.subs.push(this.userControl.valueChanges.subscribe(v => this.pickedUserId = v?.id ?? null));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  public pick(tile: HubTile): void {
    if (!this.pickedUserId) { return; }
    // The standard-CV path is admin-only (GeneratedCvPolicy::generate()
    // stays admin-only by design), so a consultant clicking a stale, not-yet-
    // hidden tile must not be able to reach it.
    if (tile === 'standard' && this.auth.isUser) { return; }
    if (tile === 'standard') {
      this.mode = 'standard'; // stays in-dialog; a Standard CV has no vacancy
      return;
    }
    const artifacts = tile === 'tailored' ? 'cv' : tile === 'letter' ? 'letter' : 'cv,letter';
    this.dialogRef?.close();
    void this.router.navigate(['/cv-generator/tailor/new'], {
      queryParams: {
        subject: this.pickedUserId,
        vacancy: this.vacancyId ?? null,
        artifacts,
        submission: this.submissionId ?? null,
      },
    });
  }

  /**
   * A Standard CV never routes through the tailor create call (#767's
   * auto-link only fires there), so this is the only place a static
   * generation can reach the submission it was launched for. attachCv()
   * already surfaces a danger toast on failure (APIService#errorify), so a
   * rejection here only needs logging before closing either way — the CV
   * itself was generated and downloaded regardless of the attach outcome.
   */
  public onGenerated(result: { id: number; download_url: string }): void {
    if (!this.submissionId || !result?.id) {
      this.close();
      return;
    }
    this.submissionService.attachCv(this.submissionId, result.id)
      .catch((err: unknown) => console.error('cv-documents-hub-dialog: attach standard CV to submission failed', err))
      .finally(() => this.close());
  }

  public close(): void {
    this.dialogRef?.close();
  }

  public userLogo(user: any): string | null {
    return user?.logo?.preview ? Globals.storage(user.logo.preview) : user?.provider_avatar || null;
  }

  public get safePickedUserId(): number {
    return this.pickedUserId as number;
  }
}
